/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import type { Dispatch } from 'preact/hooks';
import { dateReducer, type DateReducerAction } from './dateReducer';
import type { CalendarDate, EditableDateSegment } from './types';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { isBackspaceOrDelete, isSelectAll } from '#utils/PRIVATE_keyboardUtils';
import { useDoubleTap } from '#hooks/UNSAFE_useDoubleTap';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { DateFieldState } from './getDateFieldInitialReducerState';
import { InputDateMask } from './InputDateMask';
import { useInputDateMaskSelectFirstHandler } from './useInputDateMaskSelectFirstHandler';

type InputDateMaskProps = ComponentProps<typeof InputDateMask>;

type Props = {
  direction: 'ltr' | 'rtl';
  dispatch: Dispatch<DateReducerAction>;
  groupRef?: RefObject<HTMLDivElement>;
  isDisabled?: boolean;
  isInputFocused?: boolean;
  value: CalendarDate | undefined;
  hasSelectedSegment: boolean;
  isCompleteDate: boolean;
  isDateSelected: boolean;
  state: DateFieldState;
  orderedSegments: EditableDateSegment[];
  onCommit: InputDateMaskProps['onCommit'];
  onInput: InputDateMaskProps['onInput'];
};

function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}

/**
 * A hook that creates various handlers for a DateField.
 * useDateFieldHandler handles user events like click, Ctrl+A/Delete, Arrow Right, Enter etc., and it will
 * dispatch actions based on those events.
 *
 * This hook creates a dateClearedRef. It is set to false by default and whenever the
 * value isn't undefined, including a partial value.
 * When the user's action is to clear the field, this hook dispatches the 'clearDate' action,
 * and sets dateClearedRef to true.
 * dateClearedRef is returned from the hook so that the aria-live DOM can be updated to say Date Cleared
 * or to clear this text.
 */
const useDateFieldHandlers = ({
  hasSelectedSegment,
  value,
  direction,
  dispatch,
  groupRef,
  isCompleteDate,
  isDateSelected,
  isDisabled,
  isInputFocused,
  orderedSegments,
  state,
  onCommit,
  onInput
}: Props) => {
  const { tabbableModeProps } = useTabbableMode();
  const { tabIndex: tabbableModeIndex } = tabbableModeProps;

  // If any segment is selected, it is contenteditable=true and it has the focus. If you shift-Tab, you will
  // land on the outer div (DateField) first meaning it takes 2 shift-tab keys to get the previous element.
  // To prevent this, if any segment is selected the tabindex on the div is set to -1.
  // We also need to set tabindex to -1 for the disabled case, since we are dealing with a div instead
  // of an intrinsic input and have to handle it directly.
  const updatedTabIndex = tabbableModeIndex === -1 || hasSelectedSegment || isDisabled ? -1 : 0;

  // Keep track whether the date was cleared.
  const dateClearedRef = useRef<boolean>(false);
  // The dateClearedRef.current is set to true when the user does a clear all.
  // We set it back to false whenever the value is not undefined
  if (value !== undefined && Object.keys(value).length > 0) {
    dateClearedRef.current = false;
  }

  // Handle requested segment changes by calling the reducer dispatch function.
  const onChange = useCallback(
    (action: DateReducerAction) => {
      // Handle requested segment changes via the reducer's dispatch function.
      // This pattern ensures that rendered state (like segment isSelected) is updated via dispatch,
      // while also allowing us to immediately compute the resulting currentDate by re-reducing the state.
      // We then call onInput with the new value within the same event handler,
      // ensuring onInput is not called within a useLayoutEffect or other lifecycle hook.
      // This allows the parent to receive the currentDate synchronously in response to user input
      // and will re-render InputDateMask with the updated currentDate as needed.
      dispatch(action);
      const updatedState = dateReducer(state, action);
      onInput({ value: updatedState.currentDate });
    },
    [dispatch, onInput, state]
  );

  // If the user clicked on a segment, dispatch a request to select it.
  // If they clicked on the date field itself, select the first segment.
  const onClick = useCallback(
    (e: Event) => {
      const segmentType = (e.target as HTMLElement).getAttribute(
        'data-segment'
      ) as EditableDateSegment | null;

      if (segmentType !== null) {
        onChange({ actionType: 'selectSegment', data: { type: segmentType } });
      } else {
        onChange({ actionType: 'selectFirst', data: { orderedSegments } });
      }
    },
    [onChange, orderedSegments]
  );

  // If complete, select the date.
  const selectAll = useCallback(
    (isGroupFocus = true) => {
      if (isCompleteDate) {
        onChange({ actionType: 'selectDate' });
        // Focus can end up in different places depending on if the user selected a segment
        // first (via click or arrow key), and then did select all. To make shift-tabbing
        // consistent in all cases, set focus to the group when selecting the date.
        isGroupFocus && groupRef?.current?.focus();
      }
    },
    [groupRef, isCompleteDate, onChange]
  );

  const onDoubleClick = useCallback(() => {
    selectAll();
  }, [selectAll]);

  const onDoubleTap = useCallback(
    (e: Event) => {
      const segmentType = (e.target as HTMLElement).getAttribute(
        'data-segment'
      ) as EditableDateSegment;

      // Only do select all if a segment was double tapped, which means the
      // mobile keyboard will stay up.
      !!segmentType && selectAll(false);
    },
    [selectAll]
  );

  const doubleTapHandlers = useDoubleTap({ onDoubleTap: onDoubleTap, onSingleTap: onClick });

  // Handle arrow key navigation between segments. If no segment is currently selected, then select the first one.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        (direction === 'ltr' && event.key === 'ArrowLeft') ||
        (direction === 'rtl' && event.key === 'ArrowRight')
      ) {
        // Select the previous segment when pressing Left arrow (or Right arrow in rtl).
        onChange({ actionType: 'selectPrevious', data: { orderedSegments } });
        event.stopPropagation();
      } else if (
        (direction === 'ltr' && event.key === 'ArrowRight') ||
        (direction === 'rtl' && event.key === 'ArrowLeft')
      ) {
        // Select the next segment when pressing Right arrow (or Left arrow in rtl).
        onChange({ actionType: 'selectNext', data: { orderedSegments } });
        event.stopPropagation();
      } else if ((isBackspaceOrDelete(event) || event.key === 'Backspace') && isDateSelected) {
        // Clear the entire date.
        onChange?.({ actionType: 'clearDate', data: { orderedSegments } });
        dateClearedRef.current = true;
        // Kill the event to prevent weird issue with placeholders getting deleted on iOS only.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      } else if (isSelectAll(event)) {
        selectAll();
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === 'Enter') {
        // Hitting Enter commits any changes.
        dispatch({ actionType: 'commitOnEnter' });
        const updatedState = dateReducer(state, { actionType: 'commitOnEnter' });
        onInput?.({ value: updatedState.currentDate });
        onCommit?.({ value: updatedState.currentDate });
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [
      direction,
      isDateSelected,
      onChange,
      orderedSegments,
      selectAll,
      dispatch,
      state,
      onInput,
      onCommit
    ]
  );

  const onPointerDown = useInputDateMaskSelectFirstHandler({
    dispatch,
    isInputFocused,
    orderedSegments
  });

  const handlers = isMobile()
    ? { ...doubleTapHandlers, onKeyDown }
    : { onClick, onDoubleClick, onKeyDown, onPointerDown };

  return {
    dateClearedRef,
    dateFieldHandlers: isDisabled ? {} : handlers,
    segmentHandlers: isDisabled ? {} : { onChange },
    tabIndex: updatedTabIndex
  };
};

export { useDateFieldHandlers };
